import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  List,
  SxProps,
  TextField,
  Theme,
  Typography,
  useTheme
} from "@mui/material";
import { Instance as PopperInstance } from "@popperjs/core";
import { CreateNewInvestorDrawer } from "components/deal/Investors/Create-new-investor-drawer/CreateNewInvestorDrawer";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useInvestorsSelector } from "store";
import { IInvestor } from "store/types/deal/investor";

const UNGROUPED_LABEL = "No Group";

type GroupOption = {
  groupName: string;
  investors: IInvestor[];
  section: "deal" | "account";
  isUngrouped?: boolean;
  // account investors are rendered as a flat, individually selectable list (no accordion/checkbox)
  isFlat?: boolean;
};

export interface GroupSelectionValue {
  groupsName: string[];
  investorId: number | null;
  investorsIds: number[];
}

interface Props {
  sx?: SxProps<Theme>;
  disabled?: boolean;
  loading?: boolean;
  dealInvestors: { id: number; name: string; is_splittable?: boolean }[];
  // investor
  preselectedInvestorId: number | null;
  onChange: (value: { id: number; name: string } | null) => void;
  onGroupsChange?: (value: GroupSelectionValue) => void;
  //if several investors and we need to show group name instead of investor name
  preselectedInvestorIds?: number[];
  label?: string;
  error?: boolean;
  helperText?: string;
  allInvestorsId?: number;
  showHint?: boolean;
}

export const InvestorGroupAutocomplete: React.FC<Props> = ({
  disabled,
  loading,
  dealInvestors,
  preselectedInvestorId,
  onChange,
  onGroupsChange,
  preselectedInvestorIds,
  label,
  sx,
  error,
  helperText,
  allInvestorsId,
  showHint
}) => {
  const investors = useInvestorsSelector();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const popperRef = useRef<PopperInstance | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const shouldRestoreScroll = useRef(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useLayoutEffect(() => {
    if (shouldRestoreScroll.current && listboxRef.current) {
      listboxRef.current.scrollTop = scrollPositionRef.current;
      shouldRestoreScroll.current = false;
    }
  }, [expandedGroups, preselectedInvestorIds]);

  const groupOptions = useMemo((): GroupOption[] => {
    // Only deal investors that are splittable can form selectable groups (split by commitment).
    const splittableDealIds = new Set(dealInvestors.filter((d) => d.is_splittable).map((d) => d.id));

    const dealGroupsMap = new Map<string, IInvestor[]>();
    const dealInvestorsWithoutGroup: IInvestor[] = [];
    const accountInvestors: IInvestor[] = [];

    investors.forEach((investor) => {
      const isDeal = splittableDealIds.has(investor.id);

      if (isDeal) {
        if (investor.group) {
          if (!dealGroupsMap.has(investor.group)) dealGroupsMap.set(investor.group, []);
          dealGroupsMap.get(investor.group)!.push(investor);
        } else {
          dealInvestorsWithoutGroup.push(investor);
        }
      } else {
        // Everyone else (non-splittable, or not in the deal) is shown as a flat account list.
        accountInvestors.push(investor);
      }
    });

    const result: GroupOption[] = [];

    Array.from(dealGroupsMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([groupName, invs]) => result.push({ groupName, investors: invs, section: "deal" }));

    if (dealInvestorsWithoutGroup.length) {
      result.push({
        groupName: UNGROUPED_LABEL,
        investors: dealInvestorsWithoutGroup,
        section: "deal",
        isUngrouped: true
      });
    }

    accountInvestors
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((investor) =>
        result.push({ groupName: investor.name, investors: [investor], section: "account", isFlat: true })
      );

    return result;
  }, [investors, dealInvestors]);

  const selectedGroups = useMemo(() => {
    if (!preselectedInvestorIds?.length) return [];
    const idSet = new Set(preselectedInvestorIds);
    return groupOptions
      .filter((g) => !g.isUngrouped && !g.isFlat && g.investors.some((inv) => idSet.has(inv.id)))
      .map((g) => g.groupName);
  }, [preselectedInvestorIds, groupOptions]);

  const handleInvestorSelect = ({ investor_id }: { investor_id: number }) => {
    onChange({ id: investor_id, name: investors.find((investor) => investor.id === investor_id)?.name || "" });
    setIsDrawerOpen(false);
  };

  const saveScrollPosition = useCallback(() => {
    if (listboxRef.current) {
      scrollPositionRef.current = listboxRef.current.scrollTop;
      shouldRestoreScroll.current = true;
    }
  }, []);

  const handleToggleGroup = useCallback(
    (expandKey: string) => {
      saveScrollPosition();
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        if (next.has(expandKey)) {
          next.delete(expandKey);
        } else {
          next.add(expandKey);
        }
        return next;
      });
    },
    [saveScrollPosition]
  );

  /** Takes an array of group names and returns a flat array of all investor IDs belonging to those groups. */
  const resolveInvestorIds = useCallback(
    (groups: string[]) =>
      groupOptions.filter((g) => groups.includes(g.groupName)).flatMap((g) => g.investors.map((i) => i.id)),
    [groupOptions]
  );

  const handleGroupCheckboxToggle = useCallback(
    (groupName: string) => {
      saveScrollPosition();

      const isSelected = selectedGroups.includes(groupName);
      const newGroups = isSelected ? selectedGroups.filter((g) => g !== groupName) : [...selectedGroups, groupName];

      if (!isSelected) {
        onChange(null);
      }

      onGroupsChange?.({
        groupsName: newGroups,
        investorId: isSelected ? preselectedInvestorId : null,
        investorsIds: resolveInvestorIds(newGroups)
      });
    },
    [selectedGroups, preselectedInvestorId, onChange, onGroupsChange, saveScrollPosition, resolveInvestorIds]
  );

  const handleGroupInvestorClick = useCallback(
    (investor: IInvestor) => {
      saveScrollPosition();

      const isSelected = preselectedInvestorId === investor.id;
      const newInvestorId = isSelected ? null : investor.id;

      onChange(isSelected ? null : { id: investor.id, name: investor.name });
      onGroupsChange?.({
        groupsName: [],
        investorId: newInvestorId,
        investorsIds: []
      });
    },
    [preselectedInvestorId, onChange, onGroupsChange, saveScrollPosition]
  );

  const groupHelperText = useMemo(() => {
    if (error && helperText) return helperText;
    if (selectedGroups.length === 1) {
      return `Values will be split across ${selectedGroups[0]} members by commitment.`;
    }
    if (selectedGroups.length > 1) {
      return "Values will be split across selected groups by commitment.";
    }
    return helperText;
  }, [selectedGroups, error, helperText]);

  const selectedInvestor =
    preselectedInvestorId !== null && preselectedInvestorId !== allInvestorsId
      ? investors.find((inv) => inv.id === preselectedInvestorId) || null
      : null;

  const emptyValue = useMemo<GroupOption[]>(() => [], []);

  const allInvestorsEntry = useMemo(
    () => (allInvestorsId !== undefined ? dealInvestors.find((d) => d.id === allInvestorsId) : undefined),
    [dealInvestors, allInvestorsId]
  );
  const showCommitmentSection = !!allInvestorsEntry?.is_splittable;

  return (
    <>
      <Autocomplete<GroupOption, true, false, false>
        disabled={disabled}
        loading={loading}
        sx={{ width: "100%", ...sx }}
        multiple
        disableCloseOnSelect
        options={groupOptions}
        value={emptyValue}
        onChange={() => {}}
        getOptionLabel={(option) => option.groupName}
        isOptionEqualToValue={() => false}
        groupBy={(option) => (option.section === "deal" ? "DEAL INVESTORS" : "ACCOUNT INVESTORS")}
        renderGroup={(params) => (
          <Box key={params.key} component="li">
            <Box
              sx={{
                padding: "6px 8px",
                backgroundColor: "#F6F8FC",
                position: "sticky",
                top: -8,
                zIndex: 1
              }}
            >
              <Typography sx={{ fontSize: "12px", fontWeight: "bold", color: "#496682" }}>{params.group}</Typography>
            </Box>
            <ul style={{ padding: 0 }}>{params.children}</ul>
          </Box>
        )}
        renderTags={() => null}
        renderInput={(params) => {
          const { InputProps, InputLabelProps, ...other } = params;
          return (
            <TextField
              inputRef={inputRef}
              {...other}
              slotProps={{
                inputLabel: { ...InputLabelProps, shrink: true }
              }}
              InputProps={{
                ...InputProps,
                startAdornment: (
                  <>
                    {selectedGroups.map((groupName) => (
                      <MaterialTooltip key={groupName} title={groupName} isShow>
                        <Chip
                          label={groupName}
                          size="small"
                          onMouseDown={(e) => e.stopPropagation()}
                          onDelete={() => handleGroupCheckboxToggle(groupName)}
                          sx={{ maxWidth: 150, marginRight: 0.5 }}
                        />
                      </MaterialTooltip>
                    ))}
                    {preselectedInvestorId === allInvestorsId && !selectedGroups.length && (
                      <MaterialTooltip title="All investors" isShow>
                        <Chip
                          label="All investors"
                          size="small"
                          onMouseDown={(e) => e.stopPropagation()}
                          onDelete={() => {
                            onChange(null);
                            onGroupsChange?.({ groupsName: [], investorId: null, investorsIds: [] });
                          }}
                          sx={{ maxWidth: 150, marginRight: 0.5 }}
                        />
                      </MaterialTooltip>
                    )}
                    {selectedInvestor && !selectedGroups.length && (
                      <MaterialTooltip title={selectedInvestor.name} isShow>
                        <Chip
                          label={selectedInvestor.name}
                          size="small"
                          onMouseDown={(e) => e.stopPropagation()}
                          onDelete={() => {
                            onChange(null);
                            onGroupsChange?.({ groupsName: [], investorId: null, investorsIds: [] });
                          }}
                          sx={{ maxWidth: 150, marginRight: 0.5 }}
                        />
                      </MaterialTooltip>
                    )}
                    {InputProps?.startAdornment}
                  </>
                )
              }}
              label={label}
              placeholder="Select"
              error={error}
              helperText={showHint ? groupHelperText : ""}
              sx={{
                "& .MuiOutlinedInput-root": {
                  minHeight: 40,
                  maxHeight: "none",
                  height: "auto"
                }
              }}
            />
          );
        }}
        slotProps={{
          popper: {
            popperRef: popperRef,
            placement: "bottom-start",
            modifiers: [
              {
                name: "flip",
                enabled: true,
                options: {
                  fallbackPlacements: ["top-start"],
                  boundary: "viewport",
                  padding: 8
                }
              },
              { name: "preventOverflow", enabled: true, options: { rootBoundary: "viewport", padding: 8 } }
            ]
          }
        }}
        renderOption={(optionProps, option) => {
          const { key } = optionProps;
          if (option.isFlat) {
            const investor = option.investors[0];
            return (
              <li key={key} style={{ padding: 0 }}>
                <InvestorRow
                  investor={investor}
                  isSelected={preselectedInvestorId === investor.id}
                  onClick={handleGroupInvestorClick}
                />
              </li>
            );
          }
          const isSelected = selectedGroups.includes(option.groupName);
          return (
            <li key={key} style={{ padding: 0 }}>
              <GroupWithCheckbox
                groupOption={option}
                isSelected={isSelected}
                popperRef={popperRef}
                searchValue={inputRef.current?.value || ""}
                expandedGroups={expandedGroups}
                onToggleGroup={handleToggleGroup}
                onCheckboxToggle={handleGroupCheckboxToggle}
                selectedInvestorId={preselectedInvestorId}
                onInvestorClick={handleGroupInvestorClick}
                section={option.section}
              />
            </li>
          );
        }}
        ListboxProps={{
          ref: listboxRef,
          sx: { "& .MuiAutocomplete-option": { padding: 0 } }
        }}
        PaperComponent={({ children, ...paperProps }) => (
          <Box
            {...paperProps}
            sx={{
              "& .MuiAutocomplete-listbox": { maxHeight: "200px" },
              backgroundColor: "white",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
              borderRadius: "10px"
            }}
          >
            {showCommitmentSection && (
              <Button
                variant="text"
                fullWidth
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={() => {
                  onChange({ id: allInvestorsId!, name: "All investors" });
                  onGroupsChange?.({ groupsName: [], investorId: allInvestorsId!, investorsIds: [] });
                }}
                sx={{
                  backgroundColor:
                    preselectedInvestorId === allInvestorsId ? "rgba(25, 118, 210, 0.08)" : "transparent",
                  height: "45px",
                  justifyContent: "flex-start",
                  padding: "12px 16px",
                  color: "#0a3142",
                  fontWeight: 500,
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                  borderRadius: 0,
                  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" }
                }}
              >
                All investors - Split by commitment
              </Button>
            )}
            {children}
            <Button
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={() => setIsDrawerOpen(true)}
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                height: "45px",
                backgroundColor: "#fff",
                borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                borderRadius: "0 0 10px 10px",
                color: "#0a3142",
                fontWeight: 500,
                padding: "12px 16px",
                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" }
              }}
              fullWidth
            >
              Add Investor
            </Button>
          </Box>
        )}
      />
      <CreateNewInvestorDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        saveNewInvestorToSelected={(response) => {
          handleInvestorSelect({ investor_id: response.id });
        }}
      />
    </>
  );
};

// --- GroupWithCheckbox sub-component ---

interface GroupWithCheckboxProps {
  groupOption: GroupOption;
  isSelected: boolean;
  popperRef: React.RefObject<PopperInstance | null>;
  searchValue: string;
  expandedGroups: Set<string>;
  onToggleGroup: (expandKey: string) => void;
  onCheckboxToggle: (groupName: string) => void;
  selectedInvestorId: number | null;
  onInvestorClick: (investor: IInvestor) => void;
  section: "deal" | "account";
}

const GroupWithCheckbox: React.FC<GroupWithCheckboxProps> = ({
  groupOption,
  isSelected,
  popperRef,
  searchValue,
  expandedGroups,
  onToggleGroup,
  onCheckboxToggle,
  selectedInvestorId,
  onInvestorClick,
  section
}) => {
  const theme = useTheme();
  const { groupName, investors, isUngrouped } = groupOption;
  const expandKey = `${section}::${groupName}`;
  // Search expansion is derived: while the user is searching, all groups appear open without
  // mutating expandedGroups. When search clears, we fall back to the user's manual state.
  const isExpanded = expandedGroups.has(expandKey) || searchValue.length >= 2;
  const showCheckbox = !isUngrouped;

  return (
    <Accordion
      expanded={isExpanded}
      sx={{ borderRadius: "0 !important", color: "#0a3142", boxShadow: "none", width: "100%" }}
      onChange={(_, expanded) => {
        onToggleGroup(expandKey);
        if (expanded) {
          requestAnimationFrame(() => {
            setTimeout(() => {
              popperRef.current?.update();
            }, theme.transitions.duration.standard);
          });
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ArrowForwardIosIcon sx={{ fontSize: "0.9rem", fill: "#5D7782", transform: "rotate(90deg)" }} />}
        sx={{
          fontWeight: 600,
          fontSize: 15,
          "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
            transform: "rotate(180deg)"
          },
          "& .MuiAccordionSummary-content, .MuiAccordionSummary-content.Mui-expanded": {
            margin: 0,
            overflow: "hidden"
          },
          padding: "6px 12px !important",
          minHeight: "28px !important",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04) !important"
          }
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", width: "100%", overflow: "hidden", height: "30px" }}>
          {showCheckbox && (
            <Checkbox
              checked={isSelected}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                onCheckboxToggle(groupName);
              }}
              sx={{ padding: "4px", marginRight: "8px", flexShrink: 0 }}
            />
          )}
          <MaterialTooltip title={groupName} isShow>
            <Box
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "14px",
                fontWeight: 500
              }}
            >
              {groupName}
            </Box>
          </MaterialTooltip>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: "0px" }}>
        <List sx={{ padding: 0 }}>
          {investors.map((investor: IInvestor) => (
            <InvestorRow
              key={investor.id}
              investor={investor}
              isSelected={selectedInvestorId === investor.id}
              onClick={onInvestorClick}
              indent
            />
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};

// --- InvestorRow sub-component ---

interface InvestorRowProps {
  investor: IInvestor;
  isSelected: boolean;
  onClick: (investor: IInvestor) => void;
  // indent when nested inside a group accordion; flat account rows are not indented
  indent?: boolean;
}

const InvestorRow: React.FC<InvestorRowProps> = ({ investor, isSelected, onClick, indent }) => {
  const handleActivate = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    onClick(investor);
  };

  return (
    <Box
      component="li"
      role="option"
      tabIndex={0}
      aria-selected={isSelected}
      onClick={handleActivate}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleActivate(e);
        }
      }}
      sx={{
        fontSize: 14,
        margin: 0,
        padding: indent ? "6px 12px 6px 24px" : "6px 12px",
        cursor: "pointer",
        backgroundColor: isSelected ? "rgba(25, 118, 210, 0.08)" : "transparent",
        outline: "none",
        "&:hover": {
          backgroundColor: isSelected ? "rgba(25, 118, 210, 0.08)" : "rgba(0, 0, 0, 0.04)"
        },
        "&:focus-visible": {
          backgroundColor: "rgba(0, 0, 0, 0.08)"
        }
      }}
    >
      {investor.name}
    </Box>
  );
};
